import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Stack, Switch, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { FormInput, FormMediaUpload, Info, Spinner } from 'components';
import { getUser, selectUser } from 'store/user';
import { useAppDispatch, useAppSelector } from 'store';
import { ApiUser } from 'types';
import { ADMIN, CLINIC_ADMIN } from 'constants/roles';
import { IApiRequests, useApiRequests, useAppFeatureFlags } from 'hooks';

import { isHipaaLink } from '../../../../../../config/productProfile';

import styles from './OrganizationPannel.module.scss';

type OrganizationForm = {
  organization: string;
  description?: string;
  enableCallHistory: boolean;
};

export const OrganizationPanel = () => {
  const { t, i18n } = useTranslation('settings');
  const isRtl = i18n.dir() === 'rtl';
  const navigate = useNavigate();
  const location = useLocation();
  const {
    displayDescriptionInOrganizationDetailsFormFeature,
    displayOrganizationLogoInputInOrganizationDetailsFormFeature,
  } = useAppFeatureFlags();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const user: ApiUser = useAppSelector(selectUser)!;
  const { updateClinic, updateClinicPicture }: IApiRequests = useApiRequests();

  const [loading, setLoading] = useState<boolean>(false);
  const [clinicPicture, setClinicPicture] = useState<File | null>(null);
  const [clinicPictureChanged, setClinicPictureChanged] = useState<boolean>(false);
  const schema = Yup.object().shape({
    organization: Yup.string()
      .min(2, t('organizationPanel.minLength2'))
      .max(100, t('organizationPanel.maxLength100'))
      .required(t('organizationPanel.organizationRequired')),
    description: Yup.string().max(1000, t('organizationPanel.maxLength1000')).nullable(),
  });

  const isAdmin: boolean = useMemo(() => {
    return user?.clinicUser.roles.some((role: string) => role === ADMIN || role === CLINIC_ADMIN) ?? false;
  }, [user]);

  const formDefaultValues: OrganizationForm = useMemo(
    () => ({
      organization: user.clinicUser.organization,
      description: user.clinicUser.description ?? '',
      enableCallHistory: user.clinicUser.enableCallHistory ?? true,
    }),
    [user],
  );

  const form = useForm<OrganizationForm>({
    resolver: yupResolver(schema),
    defaultValues: formDefaultValues,
    mode: 'all',
  });

  useEffect(() => {
    form.reset(formDefaultValues);
  }, [formDefaultValues]);

  const handleSubmit = (): void => {
    if (!form.formState.isValid) {
      return;
    }

    setLoading(true);

    const updateImagePromise = (): Promise<void> => {
      if (!clinicPictureChanged) {
        return Promise.resolve();
      }

      return updateClinicPicture(clinicPicture).catch((error) => {
        throw new Error(`Unable to update clinic picture: ${error}`);
      });
    };

    const updateClinicPromsie = (): Promise<void> => {
      return updateClinic(formModel).catch((error) => {
        throw new Error(`Update clinic failed: ${error.message}`);
      });
    };

    const formModel: OrganizationForm = form.getValues();

    updateImagePromise()
      .then(() => updateClinicPromsie())
      .then(() => dispatch(getUser()).unwrap())
      .then(() => {
        if (location.hash !== '#yourOrganization') {
          navigate(
            {
              pathname: location.pathname,
              search: location.search,
              hash: '#yourOrganization',
            },
            { replace: true },
          );
        }
        enqueueSnackbar(t('organizationPanel.clinicUpdated'), { variant: 'success' });
      })
      .catch((error: any) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChangePicture = (picture: File | null) => {
    setClinicPicture(picture);
    setClinicPictureChanged(true);
  };

  if (loading) return <Spinner />;

  return (
    <Box className={styles.container}>
      {displayOrganizationLogoInputInOrganizationDetailsFormFeature && (
        <Stack className={styles.input}>
          <Stack direction='row' alignItems='flex-end'>
            <Typography component='p' variant='body2'>
              {t('organizationPanel.organizationLogo')}
            </Typography>
          </Stack>
          <FormMediaUpload
            onInputChange={handleChangePicture}
            onRemove={() => handleChangePicture(null)}
            title={t('organizationPanel.logo')}
            src={user.clinicUser.linkToClinicPicture ?? ''}
            onlyView={!isAdmin}
          />
        </Stack>
      )}

      <Stack className={styles.input}>
        <Typography variant='body2'>
          {isHipaaLink
            ? t('organizationPanel.providerOrganizationName')
            : t('organizationPanel.providerOrganizationNameVia3')}{' '}
          <span className={styles.asterisk}>*</span>
        </Typography>
        <FormInput name='organization' control={form.control} disabled={!isAdmin} type='text' />
      </Stack>

      {displayDescriptionInOrganizationDetailsFormFeature && (
        <Stack className={styles.input}>
          <Typography variant='body2'>{t('organizationPanel.description')}</Typography>
          <FormInput
            name='description'
            multiline
            control={form.control}
            className={styles.textArea}
            disabled={!isAdmin}
          />
        </Stack>
      )}

      <Stack className={styles.input}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Stack direction='row' alignItems='center' className={styles.labelRow}>
            <span className={styles.labelContent}>
              {isRtl ? (
                <>
                  <Info text={t('organizationPanel.enableCallHistoryInfo')} />
                  <Typography variant='body2'>{t('organizationPanel.enableCallHistory')}</Typography>
                </>
              ) : (
                <>
                  <Typography variant='body2'>{t('organizationPanel.enableCallHistory')}</Typography>
                  <Info text={t('organizationPanel.enableCallHistoryInfo')} />
                </>
              )}
            </span>
          </Stack>
          <Switch
            checked={!!form.watch('enableCallHistory')}
            onChange={(event) => form.setValue('enableCallHistory', event.target.checked, { shouldDirty: true })}
            disabled={!isAdmin}
          />
        </Stack>
      </Stack>

      {isAdmin && (
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={!form.formState.isValid || !form.formState.isDirty}
          className={styles.saveButton}
        >
          {t('organizationPanel.save')}
        </Button>
      )}
    </Box>
  );
};
